import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { VantageShellService } from 'src/app/services/vantage-shell/vantage-shell.service';
import { HPDAIOSettings } from 'src/app/data-models/smart-assist/HPD-AIO-settings.interface';
import {
	ApplicableType,
	ApproachDistanceType,
	PresenceLeaveWaitTimeType,
	PresenceLeaveDistanceType,
	PresenceLeaveActionType,
	PrivacyGuardDistanceType,
	FeatureCapabilityType,
	PrivacyGuardActionType,
} from 'src/app/enums/intelligent-security-AIO.enum';
import { CommonService } from '../../common/common.service';
@Injectable({
	providedIn: 'root',
})
export class IntelligentSecurityAIOService {
	private intelligentSensing;
	public isShellAvailable = false;
	public HPDAIOSettings: HPDAIOSettings;

	constructor(
		private logger: LoggerService,
		private vantageShellService: VantageShellService,
		private commonService: CommonService
	) {
		this.intelligentSensing = vantageShellService.getHPDAIOClient();
		if (this.intelligentSensing) {
			this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			this.isShellAvailable = true;
		}
		this.registerCallBack();
	}
	//  AIO HPD capability  isAvailable  GetHPDCapability
	/**
	 * ThinkPad Only : User Presence Sensing global toggle can be shown on UI
	 */
	public getHPDCapability(): Promise<any> {
		if (this.isShellAvailable) {
			// HPD global switch status. true means show, false means hide
			const cap = this.HPDAIOSettings.Capability;

			return Promise.resolve(
				(cap & FeatureCapabilityType.ApproachEnabled) !== 0 ||
					(cap & FeatureCapabilityType.ApproachDistanceAutoAdjust) !== 0 ||
					(cap & FeatureCapabilityType.PresenceLeaveEnabled) !== 0 ||
					(cap & FeatureCapabilityType.PresenceLeaveDistanceAutoAdjust) !== 0 ||
					(cap & FeatureCapabilityType.PrivacyGuardEnabled) !== 0 ||
					(cap & FeatureCapabilityType.PrivacyGuardDistance) !== 0
			);
		}
		return undefined;
	}

	// get HPD All setting
	public getAllSetting(): Promise<object> {
		if (this.isShellAvailable) {
			this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			return Promise.resolve(this.HPDAIOSettings);
		}
		return undefined;
	}

	// AIO GetZeroTouchLoginCapable
	public getZeroTouchLoginCapability(): Promise<boolean> {
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			// Get Auto Screen Lock section visibility
			return Promise.resolve(
				((cap & FeatureCapabilityType.ApproachEnabled) !== 0 ||
					(cap & FeatureCapabilityType.ApproachDistanceAutoAdjust) !== 0) &&
					this.HPDAIOSettings.ApproachCapable
			);
		}
		return undefined;
	}

	// AIO get Zero Touch Lock Capable  GetZeroTouchLockCapable)()
	public getZeroTouchLockCapability(): Promise<boolean> {
		// Get Auto Screen Lock section visibility
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve(
				((cap & FeatureCapabilityType.PresenceLeaveEnabled) !== 0 ||
					(cap & FeatureCapabilityType.PresenceLeaveDistanceAutoAdjust) !== 0) &&
					this.HPDAIOSettings.PresenceLeaveCapable
			);
		}
		return undefined;
	}

	// get Privacy Guard Capable
	public getPrivacyGuardCapability(): Promise<boolean> {
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve(
				((cap & FeatureCapabilityType.PrivacyGuardEnabled) !== 0 ||
					(cap & FeatureCapabilityType.PrivacyGuardDistance) !== 0) &&
					this.HPDAIOSettings.PrivacyGuardCapable
			);
		}
		return undefined;
	}

	public getApproachApplicableType(): Promise<ApplicableType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.ApproachApplicable);
		}
		return undefined;
	}
	public getPresenceLeaveApplicableType(): Promise<ApplicableType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveApplicable);
		}
		return undefined;
	}
	public getPrivacyGuardApplicableType(): Promise<ApplicableType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PrivacyGuardApplicable);
		}
		return undefined;
	}

	// AIO  ResetAllSetting
	public resetHPDSetting(): Promise<boolean> {
		if (this.isShellAvailable) {
			const errorCode = this.intelligentSensing.resetAllSetting();
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// Reset Zero Touch Login
	public resetZeroTouchLoginSetting(): Promise<boolean> {
		if (this.isShellAvailable) {
			const errorCode = this.intelligentSensing.resetApproachSetting();
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// Reset Zero Touch Lock
	public resetZeroTouchLockSetting(): Promise<boolean> {
		if (this.isShellAvailable) {
			const errorCode = this.intelligentSensing.resetPresentLeaveSetting();
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// Reset Privacy Guard
	public resetPrivacyGuardSetting(): Promise<boolean> {
		if (this.isShellAvailable) {
			const errorCode = this.intelligentSensing.resetPrivacyGuardSetting();
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	/**
	 * Set Zero Touch Login toggle button status,
	 */
	public setZeroTouchLoginStatus(value: boolean): Promise<boolean> {
		// HPD global switch status. true means enable, false means disable
		if (this.isShellAvailable) {
			const option = value ? 'True' : 'False';
			const errorCode = this.intelligentSensing.setApproachEnabled(option === 'True');
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	public getZeroTouchLoginStatus(): Promise<boolean> {
		// Get Auto Screen Login setting
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.ApproachEnabled);
		}
		return undefined;
	}
	/**
	 * Set Zero Touch Login toggle button status,
	 */

	public setZeroTouchLoginAutoAdjustStatus(value: boolean): Promise<boolean> {
		// HPD global switch status. true means enable, false means disable
		if (this.isShellAvailable) {
			const option = value ? 'True' : 'False';
			const errorCode = this.intelligentSensing.setApproachDistanceAutoAdjust(
				option === 'True'
			);
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	public getZeroTouchLoginAutoAdjustStatus(): Promise<boolean> {
		// Get Auto Screen Login setting
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.ApproachDistanceAutoAdjust);
		}
		return undefined;
	}
	public getZeroTouchLoginAutoAdjustCapability(): Promise<boolean> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.ApproachDistanceAutoAdjust) !== 0);
		}
		return undefined;
	}
	/**
	 * Get currently selected lock screen timer value
	 */
	public setZeroTouchLoginDistance(value: ApproachDistanceType): Promise<boolean> {
		// HPD global switch status. true means enable, false means disable
		if (this.isShellAvailable) {
			const option = value;
			const errorCode = this.intelligentSensing.setApproachDistance(Number(option));
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	public getZeroTouchLoginDistance(): Promise<ApproachDistanceType> {
		// Get Auto Screen Login setting
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.ApproachDistance);
		}
		return undefined;
	}
	public getZeroTouchLoginDistanceCapability(): Promise<boolean> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.ApproachDistance) !== 0);
		}
		return undefined;
	}

	// ApproachDistanceOptionsInMms
	public getZeroTouchLoginDistanceOptionsList(): Promise<any> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.ApproachDistanceOptionsInMm.split(','));
		}
		return undefined;
	}

	// set auto adjust for IdeaPad models
	public setZeroTouchLockStatus(value: boolean): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value ? 'True' : 'False';
			const errorCode = this.intelligentSensing.setPresenceLeaveEnabled(option === 'True');
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	public getZeroTouchLockStatus(): Promise<boolean> {
		// Get Auto Screen Lock setting
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveEnabled);
		}
		return undefined;
	}
	/**
	 * set Zero Touch Lock -> screen lock type
	 */
	public setSelectedScreenLockType(value: PresenceLeaveActionType): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value;
			const errorCode = this.intelligentSensing.setPresenceLeaveAction(Number(option));
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// get Zero Touch Lock -> screen lock type PresenceLeaveAction
	public getSelectedScreenLockType(): Promise<PresenceLeaveActionType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveAction);
		}
		return undefined;
	}
	public getScreenLockTypeCapability(): Promise<any> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.PresenceLeaveAction) !== 0);
		}
		return undefined;
	}

	/**
	 * Get currently selected lock screen timer value
	 */
	public setSelectedLockTimer(value: PresenceLeaveWaitTimeType): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value;
			const errorCode = this.intelligentSensing.setPresenceLeaveWaitTime(Number(option));
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	/**
	 * Get currently selected lock screen timer value
	 */
	public getSelectedLockTimer(): Promise<PresenceLeaveWaitTimeType> {
		// Get Auto Screen Lock setting
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveWaitTime);
		}
		return undefined;
	}

	public getLockWaitTimeCapability(): Promise<boolean> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.PresenceLeaveWaitTime) !== 0);
		}
		return undefined;
	}

	// get wait time list
	public getSelectedLockWaitTimeOptionsList(): Promise<any> {
		if (this.isShellAvailable) {
			return Promise.resolve(
				this.HPDAIOSettings.PresenceLeaveWaitTimeOptionsInSec.split(',')
			);
		}
		return undefined;
	}
	/**
	 * set Zero Touch Lock -> Distance
	 */
	public setSelectedLockDistance(value: PresenceLeaveDistanceType): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value;
			const errorCode = this.intelligentSensing.setPresenceLeaveDistance(Number(option));
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	// get Zero Touch Lock -> Distance PresenceLeaveDistance
	public getSelectedLockDistance(): Promise<PresenceLeaveDistanceType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveDistance);
		}
		return undefined;
	}

	public getZeroTouchLockDistanceCapability(): Promise<boolean> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.PresenceLeaveDistance) !== 0);
		}
		return undefined;
	}

	// PresenceLeaveDistanceOptionsInMms
	public getZeroTouchLockDistanceOptionsList(): Promise<any> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveDistanceOptionsInMm.split(','));
		}
		return undefined;
	}

	// set Zero Touch Lock -> Distance Auto Adjust
	public setZeroTouchLockDistanceAutoAdjust(value: boolean): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value ? 'True' : 'False';
			const errorCode = this.intelligentSensing.setPresenceLeaveDistanceAutoAdjust(
				option === 'True'
			);
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// get Zero Touch Lock -> Distance Auto Adjust PresenceLeaveDistanceAutoAdjust
	public getZeroTouchLockDistanceAutoAdjust(): Promise<boolean> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PresenceLeaveDistanceAutoAdjust);
		}
		return undefined;
	}

	public getZeroTouchLockDistanceAutoAdjustCapability(): Promise<boolean> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve(
				(cap & FeatureCapabilityType.PresenceLeaveDistanceAutoAdjust) !== 0
			);
		}
		return undefined;
	}
	// set Privacy Guard -> toggle
	public setPrivacyGuardStatus(value: boolean): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value ? 'True' : 'False';

			const errorCode = this.intelligentSensing.setPrivacyGuardEnabled(option === 'True');
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}
	// get Privacy Guard -> toggole PrivacyGuardEnabled
	public getPrivacyGuardStatus(): Promise<boolean> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PrivacyGuardEnabled);
		}
		return undefined;
	}
	/**
	 * set Privacy Guard -> Distance
	 */
	public setSelectedPrivacyGuardDistance(value: PrivacyGuardDistanceType): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value;
			const errorCode = this.intelligentSensing.setPrivacyGuardDistance(Number(option));
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// get Privacy Guard -> Distance PrivacyGuardDistance
	public getSelectedPrivacyGuardDistance(): Promise<PrivacyGuardDistanceType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PrivacyGuardDistance);
		}
		return undefined;
	}

	// Privacy Guard Distance OptionsInMms
	public getPrivacyGuardDistanceOptionsList(): Promise<any> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PrivacyGuardDistanceOptionsInMm.split(','));
		}
		return undefined;
	}

	//Privacy Guard Distance capability
	public getPrivacyGuardDistanceCapability(): Promise<boolean> {
		// HPD global switch status. true means show, false means hide
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.PrivacyGuardDistance) !== 0);
		}
		return undefined;
	}

	//Privacy Guard action capability
	public getPrivacyGuardActionCapability(): Promise<boolean> {
		if (this.isShellAvailable) {
			const cap = this.HPDAIOSettings.Capability;
			return Promise.resolve((cap & FeatureCapabilityType.PrivacyGuardAction) !== 0);
		}
		return undefined;
	}
	//Set Privacy Guard Action
	public setPrivacyGuardAction(value: PrivacyGuardActionType): Promise<boolean> {
		if (this.isShellAvailable) {
			const option = value;
			const errorCode = this.intelligentSensing.setPrivacyGuardAction(Number(option));
			if (errorCode === 0) {
				this.HPDAIOSettings = JSON.parse(this.intelligentSensing.getAllSetting());
			}
			return Promise.resolve(errorCode === 0);
		}
		return undefined;
	}

	// Get Privacy Guard Action
	public getPrivacyGuardAction(): Promise<PrivacyGuardActionType> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.PrivacyGuardAction);
		}
		return undefined;
	}

	public getWindowsHelloStatus(): Promise<boolean> {
		// get windows hello face registered status
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.FaceIdRegistered);
		}
		return undefined;
	}

	public getWindowsHelloSupportedStatus(): Promise<boolean> {
		if (this.isShellAvailable) {
			return Promise.resolve(this.HPDAIOSettings.HelloFaceSupported);
		}
		return undefined;
	}

	public registerCallBack() {
		if (this.intelligentSensing) {
			this.intelligentSensing.onstatusupdated = (value) => {
				this.HPDAIOSettings = JSON.parse(value);
				this.commonService.sendNotification('HPDAIOSettings', this.HPDAIOSettings);
			};
			this.intelligentSensing.registerCallback();
		}
	}

	public unRegisterCallBack() {
		if (this.intelligentSensing) {
			this.intelligentSensing.unRegisterCallback();
		}
	}
}
